import { useCallback, useState } from "react";
import { AiccAssetApi } from "types/model/aiccLearningAssets";
import { RunWrite } from "./useLearningAssetVersions";

interface Options<T, P> {
  api: AiccAssetApi<T, P>;
  runWrite: RunWrite<T>;
  getLabel: (value: T | P) => string;
}

export function useAssetCrud<T extends { id: string }, P>({ api, runWrite, getLabel }: Options<T, P>) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<T>();

  const openModal = useCallback((item?: T) => {
    setEditingItem(item);
    setIsModalVisible(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalVisible(false);
    setEditingItem(undefined);
  }, []);

  const handleDelete = useCallback(
    (item: T) => {
      runWrite((languageId) => api.remove(languageId, item.id), `Deleted ${getLabel(item)} successfully.`);
    },
    [api, getLabel, runWrite]
  );

  const handleSubmit = useCallback(
    (payload: P) => {
      runWrite(
        (languageId) =>
          editingItem ? api.update(languageId, editingItem.id, payload) : api.create(languageId, payload),
        `${editingItem ? "Updated" : "Added"} ${getLabel(payload)} successfully.`,
        closeModal
      );
    },
    [api, closeModal, editingItem, getLabel, runWrite]
  );

  return { isModalVisible, editingItem, openModal, closeModal, handleDelete, handleSubmit };
}
